import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Alert, AlertTitle, Paper, Chip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import RichTextEditor from '../common/RichTextEditor';
import InvalidKeysAlert from '../templates/InvalidKeysAlert';
import { getInvalidKeys } from '../../utils/templateKeyUtils';

/**
 * ContentTab - Template content editor with CKEditor 5
 * 
 * Features:
 * - CKEditor 5 RichTextEditor integration
 * - {{key}} mention support (type { to see suggestions)
 * - Question keys display for reference
 * - Auto-save support (via parent component)
 * - Word-like rich text editing (50+ fonts, tables, images, etc.)
 * 
 * Phase 6A: T4.16 - ContentTab with CKEditor 5
 */
const ContentTab = ({ content, questions = [], onChange }) => {
  // Get invalid keys from content (for warning)
  const invalidKeys = useMemo(() => {
    return getInvalidKeys(content);
  }, [content]);
  // Get unique question keys for mention feed
  const questionKeys = useMemo(() => {
    return questions
      .filter((q) => q.key && q.key.trim() !== '')
      .map((q) => q.key)
      .filter((key, index, self) => self.indexOf(key) === index); // Unique keys
  }, [questions]);

  // Configure mention feed for {{key}} insertion
  // Type { in editor to see question key suggestions
  const mentionFeeds = useMemo(() => {
    if (questionKeys.length === 0) {
      return null;
    }

    return [
      {
        marker: '{',
        feed: (queryText) => {
          // Filter question keys based on query
          const filtered = questionKeys.filter((key) =>
            key.toLowerCase().includes(queryText.toLowerCase())
          );
          return filtered;
        },
        outputText: (key) => `{{${key}}}`
      }
    ];
  }, [questionKeys]);

  // Handle editor data change
  const handleEditorChange = (data) => {
    if (onChange) {
      onChange(data);
    }
  };

  // Key Naming Rules Info Component (always visible)
  const KeyNamingRulesInfo = () => (
    <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, mx: 2, mt: 2 }}>
      <AlertTitle sx={{ fontSize: '0.875rem' }}>Key Naming Kuralları</AlertTitle>
      <Typography variant="caption" component="div" sx={{ fontSize: '0.75rem' }}>
        <strong>✓ Geçerli Key Formatı:</strong>
        <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em', fontSize: '0.75rem' }}>
          <li>Harf ile başlamalı (A-Z, a-z)</li>
          <li>Harf, rakam, alt çizgi (_) içerebilir</li>
          <li>Türkçe karakter yasak (ğ, ı, ö, ü, ş, ç)</li>
          <li>Boşluk yasak (alt çizgi kullanın)</li>
          <li>Özel karakter yasak (-, ., !, ?, vb.)</li>
        </ul>
        <strong>Örnekler:</strong>{' '}
        <code>{'{{Ad}}'}</code>,{' '}
        <code>{'{{Soyad}}'}</code>,{' '}
        <code>{'{{TC_No}}'}</code>,{' '}
        <code>{'{{Firma_Adi}}'}</code>
      </Typography>
    </Alert>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Key Naming Rules Info (always visible) */}
      <KeyNamingRulesInfo />
      
      {/* Invalid Keys Alert (shows when invalid keys found) */}
      <Box sx={{ mx: 2 }}>
        <InvalidKeysAlert invalidKeys={invalidKeys} />
      </Box>

      {/* Info Section - Compact */}
      {questionKeys.length > 0 ? (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', mx: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoIcon fontSize="small" color="action" />
              <Typography variant="caption" fontWeight="medium">
                Soru Anahtarları:
              </Typography>
            </Box>
            {questionKeys.slice(0, 5).map((key) => (
              <Chip
                key={key}
                label={`{{${key}}}`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace', height: 24, fontSize: '0.75rem' }}
              />
            ))}
            {questionKeys.length > 5 && (
              <Typography variant="caption" color="text.secondary">
                +{questionKeys.length - 5} daha
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Editörde <code>{'{'}</code> tuşuna basarak ekleyebilirsiniz
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ mx: 2, mt: 2, mb: 2 }}>
          Henüz soru tanımlanmadı. Önce "Sorular" sekmesinden sorular ekleyin.
        </Alert>
      )}

      {/* Rich Text Editor - Full Height */}
      <Box sx={{ flex: 1, px: 2, pb: 2 }}>
        <RichTextEditor
          value={content || ''}
          onChange={handleEditorChange}
          mentionFeeds={mentionFeeds}
          className="template-content-editor full-height"
        />
      </Box>
    </Box>
  );
};

ContentTab.propTypes = {
  content: PropTypes.string,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      displayText: PropTypes.string
    })
  ),
  onChange: PropTypes.func.isRequired
};

ContentTab.defaultProps = {
  content: '',
  questions: []
};

export default ContentTab;
